import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, BookOpen, Sparkles, Book } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import { toast } from 'sonner';

const PublishHub = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast.error('Please sign in to publish content');
      navigate('/auth');
    }
  }, [loading, isAuthenticated, navigate]);

  const publishOptions = [
    {
      id: 'comic',
      title: 'Create Comic Series',
      description: 'Start a new webcomic or manga series',
      icon: BookOpen,
      color: 'from-orange-500 to-red-500',
      hoverColor: 'hover:from-orange-600 hover:to-red-600',
      route: '/create-series'
    },
    {
      id: 'novel',
      title: 'Upload Novel',
      description: 'Publish chapters of your original novel or light novel',
      icon: Book,
      color: 'from-green-500 to-emerald-500',
      hoverColor: 'hover:from-green-600 hover:to-emerald-600',
      route: '/create-novel'
    },
    {
      id: 'art',
      title: 'Upload Artwork',
      description: 'Share your digital art, illustrations, and character designs',
      icon: Palette,
      color: 'from-purple-500 to-pink-500',
      hoverColor: 'hover:from-purple-600 hover:to-pink-600',
      route: '/upload-artwork'
    }
    // Music and streaming features will be added in future updates
  ];

  const handlePublishChoice = (option) => {
    if (option.comingSoon) {
      return;
    }
    navigate(option.route);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-3">What are you publishing today?</h1>
            <p className="text-gray-400 text-lg">Choose a content type to get started</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {publishOptions.map((option) => {
            const Icon = option.icon;
            return (
              <Card
                key={option.id}
                className={`bg-gray-900 border-gray-800 hover:border-gray-700 transition-all cursor-pointer group ${
                  option.comingSoon ? 'opacity-60' : ''
                }`}
                onClick={() => handlePublishChoice(option)}
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-r ${option.color} ${option.hoverColor} flex items-center justify-center transition-all group-hover:scale-110`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-white">{option.title}</h3>
                        {option.comingSoon && (
                          <span className="px-2 py-0.5 bg-yellow-500/10 text-yellow-500 text-xs font-medium rounded-full">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 mb-4">{option.description}</p>

                      {!option.comingSoon && (
                        <Button
                          className={`bg-gradient-to-r ${option.color} ${option.hoverColor} text-white`}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePublishChoice(option);
                          }}
                        >
                          Get Started
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-2xl">
            <h3 className="text-lg font-semibold mb-2">First time publishing?</h3>
            <p className="text-gray-400 text-sm mb-4">
              Each content type has its own workflow optimized for the best creator experience.
              Your content will appear on your unified profile at <span className="text-orange-500">/u/[your-handle]</span>
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-400">Auto-save</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-400">Draft support</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-400">IP ownership</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishHub;
